/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {ReactElement, ReactNode} from 'react';
import {documentToReactComponents} from '@contentful/rich-text-react-renderer';
import {BLOCKS, INLINES, Document} from '@contentful/rich-text-types';
import {useIntl} from 'react-intl';
import {cx} from '@emotion/css';

import H, {HeaderType} from '../Headers/Headers';
import P, {FontSize} from '../Paragraph/Paragraph';
import Button from '../../Buttons/Button/Button';
import getIdNameFromLabel from '../../../../contentful/getIdNameFromLabel';
import getAccordion from '../../../../contentful/getAccordion';
import DynamicLink from '../../../DynamicLink/DynamicLink';
import {useLocale} from '../../../../RootProvider/LocaleContext';
import {Alignment, ContentfulContent} from '../../../../types';
import EmbeddableAsset from '../../EmbeddableAsset/EmbeddableAsset';
import addRefsToRichText from './addRefsToRichText';
import getCtaButtonVariant from './getCtaButtonVariant';

import styles from './RichText.styles';

interface ContentfulNode {
  content?: any;
  data?: any;
}

interface Props {
  className?: string;
  content?: ContentfulContent;
}

function RichText({className, content}: Props): ReactElement | null {
  const {formatMessage} = useIntl();
  const locale = useLocale();

  if (!content) {
    return null;
  }

  const richTextWithRefs = addRefsToRichText(content) as Document;

  const options = {
    renderNode: {
      [BLOCKS.HEADING_2]: (node: ContentfulNode, children: any) => {
        const id = node.content[0].value;
        return (
          <H className={cx(styles.heading)} headerType={HeaderType.H2} alignment={Alignment.LEFT} id={getIdNameFromLabel(id)}>
            {children}
          </H>
        );
      },
      [BLOCKS.HEADING_3]: (node: ContentfulNode, children: any) => (
        <H className={cx(styles.heading)} headerType={HeaderType.H3} alignment={Alignment.LEFT}>
          {children}
        </H>
      ),
      [BLOCKS.HEADING_4]: (node: ContentfulNode, children: any) => (
        <H className={cx(styles.heading)} headerType={HeaderType.H4} alignment={Alignment.LEFT}>
          {children}
        </H>
      ),
      [BLOCKS.PARAGRAPH]: (_node: ContentfulNode, children: any) => <P>{children}</P>,
      [BLOCKS.EMBEDDED_ASSET]: (node: ContentfulNode) => {
        if (node.data.target.sys && node.data.target.sys.file) {
          const {url, title, description} = node.data.target.sys.file;
          if (url) {
            return (
              <div className={cx(styles.imageContainer)}>
                <img src={url} alt={title} data-testid="ui.richTextImage" />
                {description && (
                  <P fontSize={FontSize.SMALL} italic className={cx(styles.imageDescription)}>
                    {description}
                  </P>
                )}
              </div>
            );
          }
        }
        return null;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: ContentfulNode) => {
        if (node.data.target.sys.accordion) {
          return getAccordion({node, locale, formatMessage, nestedClass: styles.nested});
        }

        if (node.data.target.sys.contentType?.sys?.id === 'ctaButtons') {
          const {title, linkUrl, variant} = node.data.target.sys;
          return (
            <Button variant={getCtaButtonVariant(variant)} href={linkUrl} ariaLabel={title}>
              {title}
            </Button>
          );
        }

        if (node.data.target.sys.file && node.data.target.sys.file.embedType) {
          return (
            <EmbeddableAsset
              title={node.data.target.sys.file.title}
              type={node.data.target.sys.file.embedType}
              url={node.data.target.sys.file.url}
            />
          );
        }

        return null;
      },
      [BLOCKS.QUOTE]: (_node: ContentfulNode, children: ReactNode) => (
        // @todo abstract this
        <div className={cx(styles.blockQuote)}>{children}</div>
      ),
      [INLINES.HYPERLINK]: (node: ContentfulNode, children: any) => {
        if (node.data.uri) {
          return <DynamicLink href={node.data.uri}>{children}</DynamicLink>;
        }
        return null;
      },
    },
  };

  return (
    <div className={cx(styles.container, className)}>{documentToReactComponents(richTextWithRefs, options)}</div>
  );
}

export default RichText;
